import React, { PropsWithChildren, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css, useTheme } from '@emotion/react';
import AnimateHeight from 'react-animate-height';

import { LINES_TO_CLAMP } from './constants';

import { useAnimationState } from 'animations/hooks';
import { filterProps } from 'utils/helpers';
import { Classname } from 'utils/types';

interface ILineClamp extends Classname, PropsWithChildren {
  isDark?: boolean;
  type?: keyof JSX.IntrinsicElements;
  lines?: number;
}

export enum LINE_CLAMP_VARIABLES {
  color = '--color',
  rotate = '--rotate',
  translateY = '--translateY',
}
enum LINE_CLAMP_AREAS {
  button = 'button',
}

const LineClampContainer = styled('div', filterProps('isDark', 'type', 'lines', 'expanded'))<
  Pick<ILineClamp, 'isDark' | 'type' | 'lines'> & { expanded: boolean }
>`
  ${({ theme: { colors, mq }, isDark, type, lines, expanded }) => css`
    ${LINE_CLAMP_VARIABLES.color}: ${isDark ? colors.black : colors.white};

    display: contents;

    ${mq.md} {
      ${LINE_CLAMP_VARIABLES.rotate}: ${expanded ? 45 : -135}deg;
      ${LINE_CLAMP_VARIABLES.translateY}: ${expanded ? -25 : -75}%;

      display: grid;
      grid-template-areas: '.' '${LINE_CLAMP_AREAS.button}';
      row-gap: 2rem;

      ${type} {
        display: -webkit-box;
        opacity: 1;
        -webkit-box-orient: vertical;
        overflow-y: hidden;

        ${expanded
          ? ''
          : css`
              -webkit-line-clamp: ${lines};
            `}
      }
    }
  `}
`;

const Button = styled.label`
  ${({ theme: { durations, mq } }) => css`
    display: none;

    ${mq.md} {
      box-sizing: border-box;
      display: block;
      position: relative;
      grid-area: ${LINE_CLAMP_AREAS.button};
      justify-self: flex-end;
      opacity: 1;
      margin-bottom: -50%;
      border-radius: 1rem;
      cursor: pointer;
      width: 100%;
      height: 4rem;

      &::after {
        display: block;
        position: absolute;
        top: 50%;
        right: 0;
        transform: translate(-100%, var(${LINE_CLAMP_VARIABLES.translateY}))
          rotate(var(${LINE_CLAMP_VARIABLES.rotate}));
        transition: transform ${durations.regular};
        border: 1px solid var(${LINE_CLAMP_VARIABLES.color});
        border-right: initial;
        border-bottom: initial;
        width: 1rem;
        content: '';
        aspect-ratio: 1 / 1;
      }

      &:hover {
        opacity: 0.7;
      }

      &:active {
        opacity: 0.9;
      }
    }
  `}
`;

const LineClamp: React.FC<ILineClamp> = ({
  className,
  children,
  isDark = false,
  type = 'p',
  lines = LINES_TO_CLAMP,
}) => {
  // const contentHeight = useRef<number | 'auto'>('auto');
  const contentRef = useRef<HTMLDivElement>(null);

  const { durations } = useTheme();
  const { refreshed, enabled } = useAnimationState('md');

  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');
  const [expanded, setExpanded] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(false);
  const [animationExpanded, setAnimationExpanded] = useState(false);

  const duration = enabled ? Number(durations.regular.slice(0, -2)) : 0;

  useLayoutEffect(() => {
    if (enabled) {
      setExpanded(false);
      setTimeout(() => setContentHeight(contentRef.current?.offsetHeight ?? 'auto'), duration + 50);
    }
  }, [refreshed, enabled]);

  useLayoutEffect(() => {
    if (expanded) {
      setContentExpanded(expanded);
      setAnimationExpanded(expanded);
    } else {
      setAnimationExpanded(expanded);
      setTimeout(() => setContentExpanded(expanded), duration);
    }
  }, [expanded]);

  return (
    <LineClampContainer
      isDark={isDark}
      type={type}
      lines={lines}
      expanded={contentExpanded}
      className={className}
    >
      <AnimateHeight
        height={!enabled || animationExpanded ? 'auto' : contentHeight}
        duration={duration}
      >
        <div ref={contentRef}>{children}</div>
      </AnimateHeight>
      <Button onClick={() => setExpanded(expanded => !expanded)} />
    </LineClampContainer>
  );
};

export default LineClamp;
